import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { usePageSeo } from '../lib/seo';

const LAST_UPDATED = '14 December 2025';
const CONTACT_EMAIL = 'info@findmeagent.com.au';

const SECTIONS = [
  {
    title: '1. Who we are',
    body: [
      'FindMeAgent ("we", "us", "our") operates the website findmeagent.com.au. We are a free referral service that connects property owners, buyers and investors with licensed real estate agents in Australia.',
      'We are bound by the Privacy Act 1988 (Cth) and the Australian Privacy Principles (APPs). This policy explains what personal information we collect, how we use it, who we share it with and how you can access or correct it.',
    ],
  },
  {
    title: '2. What personal information we collect',
    body: ['When you use our website or forms we may collect:'],
    list: [
      'Your full name, mobile number and email address',
      'The address of the property you want to sell, buy or appraise, including suburb, state and postcode',
      'Any additional details you choose to give us (for example property type, timeframe or a message)',
      'For real estate agents applying to join our network: name, agency, licence number, years of experience, service areas, phone and email',
      'Technical data such as your IP address, browser type, device, pages visited and how you arrived at our site (for example from a Google search or advertisement)',
    ],
  },
  {
    title: '3. How we collect it',
    body: [
      'We collect information directly from you when you complete our contact or agent application forms, email us or call us. We also collect technical data automatically through cookies and similar technologies described in section 7.',
    ],
  },
  {
    title: '4. Why we collect and use your information',
    body: ['We collect and use your personal information to:'],
    list: [
      'Match you with suitable licensed real estate agents in your area and introduce you to them',
      'Contact you by phone, SMS or email about your request and the agents we have shortlisted',
      'Send you a confirmation of your enquiry and relevant follow-up information',
      'Assess and onboard real estate agents who apply to join our network',
      'Operate, secure and improve our website and measure the effectiveness of our advertising',
      'Comply with our legal obligations and resolve disputes',
    ],
    after: ['We do not sell your personal information to anyone.'],
  },
  {
    title: '5. Who we share your information with',
    body: ['We may share your personal information with:'],
    list: [
      'Real estate agents and agencies we introduce you to, so they can contact you about your property. We share only the details needed for that purpose (typically your name, contact details and property address).',
      'Service providers who help us run our business, such as website hosting, database, email delivery, analytics, advertising measurement, spam protection and address lookup providers (for example Google). These providers may store data outside Australia, including in the United States, and are required to protect it.',
      'Government bodies, regulators or law enforcement where required by law.',
    ],
    after: ['Real estate agents who receive your details are independent businesses and are responsible for their own handling of your information under their privacy policies.'],
  },
  {
    title: '6. How we store and protect your information',
    body: [
      'Your information is stored in secure, access-controlled databases. We use encryption in transit (HTTPS), restricted administrator access and spam protection on our forms. No method of transmission or storage is completely secure, but we take reasonable steps to protect your information from misuse, interference, loss, unauthorised access, modification or disclosure.',
      'We keep personal information only for as long as it is needed for the purposes above or as required by law, after which it is deleted or de-identified.',
    ],
  },
  {
    title: '7. Cookies, analytics and advertising',
    body: ['Our website uses the following third-party technologies:'],
    list: [
      'Google Analytics 4 to understand how visitors use our site (pages viewed, form submissions). Data is aggregated and does not identify you personally.',
      'Google Ads conversion tracking so we can measure which advertisements lead to enquiries.',
      'Google reCAPTCHA to protect our forms from spam and abuse. Use of reCAPTCHA is subject to the Google Privacy Policy and Terms of Service.',
      'Google Places to suggest addresses as you type in our form.',
    ],
    after: [
      'You can control or delete cookies through your browser settings, and opt out of Google Analytics using the Google Analytics opt-out browser add-on. Disabling cookies may affect some website features.',
    ],
  },
  {
    title: '8. Marketing communications',
    body: [
      'We may contact you about your enquiry and related services. You can opt out of marketing messages at any time by replying to the email, using the unsubscribe link where provided, or emailing us at the address below. We will still send you messages that are necessary to fulfil your request.',
    ],
  },
  {
    title: '9. Access, correction and complaints',
    body: [
      'You may request access to the personal information we hold about you, ask us to correct it, or ask us to delete it. Email us at the address below and we will respond within a reasonable time, usually within 30 days.',
      'If you believe we have breached the Australian Privacy Principles, please contact us first so we can try to resolve your concern. If you are not satisfied with our response, you may complain to the Office of the Australian Information Commissioner (OAIC) at oaic.gov.au.',
    ],
  },
  {
    title: '10. Changes to this policy',
    body: [
      'We may update this policy from time to time. The latest version will always be published on this page with the date it was last updated.',
    ],
  },
];

export default function PrivacyPolicy() {
  usePageSeo({
    title: 'Privacy Policy | FindMeAgent',
    description: 'How FindMeAgent collects, uses, stores and shares your personal information, in line with the Australian Privacy Principles.',
    canonical: 'https://findmeagent.com.au/privacy-policy',
    robots: 'noindex, follow',
  });

  return (
    <div className="legal-page" data-testid="privacy-policy-page">
      <header className="thankyou-topbar legal-topbar">
        <Link to="/" className="header-logo" data-testid="privacy-logo-link">
          <div className="logo-icon"><span className="logo-text">FM</span></div>
          <span className="logo-name">FINDMEAGENT</span>
        </Link>
        <Link to="/" className="legal-back" data-testid="privacy-back-link">
          <ArrowLeft size={16} /> Back to home
        </Link>
      </header>

      <main className="legal-main">
        <article className="legal-card">
          <p className="thankyou-eyebrow">Legal</p>
          <h1 className="legal-title" data-testid="privacy-title">Privacy Policy</h1>
          <p className="legal-updated">Last updated: {LAST_UPDATED}</p>

          {SECTIONS.map((s) => (
            <section key={s.title} className="legal-section">
              <h2 className="legal-heading">{s.title}</h2>
              {s.body.map((p) => <p key={p} className="legal-text">{p}</p>)}
              {s.list && (
                <ul className="legal-list">
                  {s.list.map((item) => <li key={item}>{item}</li>)}
                </ul>
              )}
              {s.after?.map((p) => <p key={p} className="legal-text">{p}</p>)}
            </section>
          ))}

          <section className="legal-section legal-contact" data-testid="privacy-contact">
            <h2 className="legal-heading">11. Contact us</h2>
            <p className="legal-text">
              Privacy enquiries, access or correction requests and complaints can be sent to{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="legal-link">{CONTACT_EMAIL}</a>.
            </p>
            <p className="legal-text">FindMeAgent, Australia · findmeagent.com.au</p>
          </section>
        </article>
      </main>
    </div>
  );
}
